import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { get, post } from '../http';
import { activeSnack } from './common';

// --- API THUNKS START ---

export const getAllCampaign = createAsyncThunk(
  'campaign/list',
  async ({ page, limit, search, dispatch }) => {
    try {
      const response = await post(
        `/campaign/advertiser/all?search=${search || ''}&page=${page || 1}&limit=${limit || 10}`
      );
      if (response) {
        return response;
      }
      return dispatch(activeSnack({ type: 'error', message: 'something went wrong' }));
    } catch (error) {
      dispatch(activeSnack({ type: 'error', message: error?.response?.data?.message }));
      throw error?.response?.data?.message;
    }
  }
);

export const getAllCampaignsList = createAsyncThunk(
  'campaign/list',
  async ({ campaignCredentials, query }) => {
    try {
      const URL = `campaign/advertiser/analytics?page=${query?.page || 1}&limit=${
        query?.limit || 25
      }&from=${query?.date?.from || ''}&to=${query?.date?.to || ''}`;
      const response = await post(URL, campaignCredentials);
      if (response) {
        return response;
      }
      return query?.dispatch(activeSnack({ type: 'error', message: 'something went wrong' }));
    } catch (error) {
      query?.dispatch(activeSnack({ type: 'error', message: error?.response?.data?.message }));
      throw error?.response?.data?.message;
    }
  }
);

export const getCampaignsDetails = createAsyncThunk(
  'campaign/details',
  async (campaignId, dispatch) => {
    try {
      const URL = `campaign/advertiser/single/${campaignId}`;
      const response = await get(URL);
      if (response) {
        return response;
      }
      return dispatch(activeSnack({ type: 'error', message: 'something went wrong' }));
    } catch (error) {
      dispatch(activeSnack({ type: 'error', message: error?.response?.data?.message }));
      throw error?.response?.data?.message;
    }
  }
);

export const getAllAdGroupListFromCampaign = createAsyncThunk(
  'campaign/adGroup/list',
  async ({ campaignCredentials, query }) => {
    try {
      const URL = `ad-group/advertiser/analytics?page=${query?.page || 1}&limit=${
        query?.limit || 25
      }&from=${query?.date?.from || ''}&to=${query?.date?.to || ''}`;
      const response = await post(URL, campaignCredentials);
      if (response) {
        return response;
      }
      return query?.dispatch(activeSnack({ type: 'error', message: 'something went wrong' }));
    } catch (error) {
      query?.dispatch(activeSnack({ type: 'error', message: error?.response?.data?.message }));
      throw error?.response?.data?.message;
    }
  }
);

export const getAllSearchTermFromCampaign = createAsyncThunk(
  'campaign/search-term/list',
  async ({ searchTermCredentials, query }) => {
    try {
      const URL = `search-term/advertiser/analytics?page=${query?.page || 1}&limit=${
        query?.limit || 25
      }&from=${query?.date?.from || ''}&to=${query?.date?.to || ''}`;
      const response = await post(URL, searchTermCredentials);
      if (response) {
        return response;
      }
      return query?.dispatch(activeSnack({ type: 'error', message: 'something went wrong' }));
    } catch (error) {
      query?.dispatch(activeSnack({ type: 'error', message: error?.response?.data?.message }));
      throw error?.response?.data?.message;
    }
  }
);

export const getAllNegativeTargetFromCampaign = createAsyncThunk(
  'campaign/negative-target/list',
  async ({ negativeTargetCredentials, query }) => {
    try {
      const URL = `negative-targeting-clause/advertiser/analytics?page=${query?.page || 1}&limit=${
        query?.limit || 25
      }&from=${query?.date?.from || ''}&to=${query?.date?.to || ''}`;
      const response = await post(URL, negativeTargetCredentials);
      if (response) {
        return response;
      }
      return query?.dispatch(activeSnack({ type: 'error', message: 'something went wrong' }));
    } catch (error) {
      query?.dispatch(activeSnack({ type: 'error', message: error?.response?.data?.message }));
      throw error?.response?.data?.message;
    }
  }
);

// --- REDUX SLICE START ---

export const campaignSlice = createSlice({
  name: 'campaign',
  initialState: {
    loading: false,
    error: {},
    campaigns: {},
    campaignDetails: {},
    allCampaignLists: {},
    allAdGroupInCampaign: {},
    allSearchTermInCampaign: {},
    allNegTargetInCampaign: {},
  },
  reducers: {
    clearStaffError: (state) => {
      state.error = true;
    },
  },
  extraReducers: {
    // get all campaigns dropdowns
    [getAllCampaign.pending]: (state) => {
      state.loading = true;
    },
    [getAllCampaign.fulfilled]: (state, action) => {
      state.loading = false;
      state.campaigns = action.payload;
      state.error = {};
    },
    [getAllCampaign.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },

    // all lists
    [getAllCampaignsList.pending]: (state) => {
      state.loading = true;
    },
    [getAllCampaignsList.fulfilled]: (state, action) => {
      state.loading = false;
      state.allCampaignLists = action.payload;
      state.error = {};
    },
    [getAllCampaignsList.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },

    // singleView
    [getCampaignsDetails.pending]: (state) => {
      state.loading = true;
    },
    [getCampaignsDetails.fulfilled]: (state, action) => {
      state.loading = false;
      state.campaignDetails = action.payload;
      state.error = {};
    },
    [getCampaignsDetails.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },

    // adGroup list in campaign
    [getAllAdGroupListFromCampaign.pending]: (state) => {
      state.loading = true;
    },
    [getAllAdGroupListFromCampaign.fulfilled]: (state, action) => {
      state.loading = false;
      state.allAdGroupInCampaign = action.payload;
      state.error = {};
    },
    [getAllAdGroupListFromCampaign.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },

    // search term in campaign
    [getAllSearchTermFromCampaign.pending]: (state) => {
      state.loading = true;
    },
    [getAllSearchTermFromCampaign.fulfilled]: (state, action) => {
      state.loading = false;
      state.allSearchTermInCampaign = action.payload;
      state.error = {};
    },
    [getAllSearchTermFromCampaign.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },

    // negative target in campaign
    [getAllNegativeTargetFromCampaign.pending]: (state) => {
      state.loading = true;
    },
    [getAllNegativeTargetFromCampaign.fulfilled]: (state, action) => {
      state.loading = false;
      state.allNegTargetInCampaign = action.payload;
      state.error = {};
    },
    [getAllNegativeTargetFromCampaign.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },
  },
});
export const { clearStaffError } = campaignSlice.actions;

export default campaignSlice.reducer;
